import React, {useRef, useState} from "react";
import {useOutsideAlerter} from "../Utility/useOutsideAlerter";
import {InventoryItem} from "../State/Inventory";
import {FaPlus} from "react-icons/fa";
import styled from "styled-components";
import {InventoryQuantity} from "../State/InventoryContext";
import {useCheatKey} from "../Utility/useCheatKey";

export type CheatPlacementOptions = { isCheat: boolean };

const M = {
    EmptyContainer: styled.div<{ $clickable?: boolean }>`
      background-color: lightgrey;
      border-radius: 8px;
      border: 4px solid lightgrey;
      width: 90px;
      height: 100px;
      margin-right: 16px;
      margin-bottom: 16px;
      align-items: center;
      display: flex;
      justify-content: center;

      ${(props) => ((props.$clickable) &&
              `
            border: 4px solid white;
              :hover {
                cursor: pointer;
              }
              box-shadow: 2px 2px;
        `
      )
      }
    `,
    EmptyMinerPopup: styled.div<{ $show: boolean }>`
      background-color: white;
      border-radius: 4px;
      border: 2px solid black;
      position: absolute;
      display: none;
      height: fit-content;
      width: 200px;
      box-shadow: 2px 2px 2px 1px rgba(0, 0, 0, 0.2);
      ${(props) => props.$show && `display: block`}

    `,
    PopupOption: styled.div<{ $disabled: boolean }>`
      padding: 4px;
      ${(props) =>
              props.$disabled ?
                      `color: grey;
               : hover { cursor: default; }
               ` :
                      `:hover {
                  background-color: lightcyan;
                  font-weight: bold;
                }`
      }
    `,
    PopupText: styled.div`
    `,
    CraftHint: styled.div`
      color: #444;
      font-size: 12px;
    `,
};

function PopupOption(props: {
    disabled: boolean,
    item: InventoryItem,
    callback?: (val: string, options?: CheatPlacementOptions) => void,
    onRequestCraft?: (val: string) => void,
    quantity: number,
    cheatActive: boolean,
}) {
    const handleClick = () => {
        const isCheatClick = props.cheatActive;
        if ((props.quantity > 0 || isCheatClick) && props.callback) {
            props.callback(props.item.id, {isCheat: isCheatClick});
            return;
        }
        if (props.quantity === 0 && props.onRequestCraft) {
            props.onRequestCraft(props.item.id);
        }
    };

    return <M.PopupOption $disabled={props.disabled}>
        <M.PopupText onClick={handleClick}>
            {props.item.name} ({props.quantity})
        </M.PopupText>
        {props.quantity === 0 && props.onRequestCraft && <M.CraftHint>Click to craft</M.CraftHint>}
    </M.PopupOption>;
}

export function EmptyMinerPopup(props: {
    isOpen: boolean,
    callback: (val: string, options?: CheatPlacementOptions) => void,
    options: InventoryQuantity[],
    onRequestCraft?: (val: string) => void,
    cheatActive?: boolean,
}) {
    const handlePopupClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation(); // Stop event propagation to parent container
    };

    return <M.EmptyMinerPopup $show={props.isOpen} onClick={handlePopupClick}>
        {props.options.map((option, index) =>
            <PopupOption key={index}
                         disabled={option.quantity === 0}
                         item={option.item}
                         quantity={option.quantity}
                         callback={props.callback}
                         onRequestCraft={props.onRequestCraft}
                         cheatActive={!!props.cheatActive}
            />
        )}
    </M.EmptyMinerPopup>;
}

export function EmptySlot(props: { type: string, clickable: boolean, options?: InventoryQuantity[], addedItem?: (val: string, options?: CheatPlacementOptions) => void, onRequestCraft?: (val: string) => void }) {
    const [popupIsOpen, setPopupIsOpen] = useState(false);
    const wrapperRef = useRef(null);
    const cheatActive = useCheatKey("c");
    const closePopup = () => {
        setPopupIsOpen(false);
    };
    useOutsideAlerter(wrapperRef, closePopup);

    const addedItem = (itemId: string, options?: CheatPlacementOptions) => {
        props.addedItem && props.addedItem(itemId, options);
    };

    return <M.EmptyContainer
        $clickable={props.clickable}
        onClick={() => props.clickable && setPopupIsOpen(true)}
        ref={wrapperRef}
        aria-label={props.clickable ? `Add miner to ${props.type}` : undefined}
        data-testid={props.clickable ? `${props.type}-add-slot` : undefined}
    >
        {props.options && <EmptyMinerPopup isOpen={popupIsOpen} callback={addedItem} options={props.options}
                                          onRequestCraft={props.onRequestCraft}
                                          cheatActive={cheatActive}/> }
        <FaPlus size={"40"} color={props.clickable ? "white" : "grey"}/>
    </M.EmptyContainer>;
}
